'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';

import { DisplayUser } from '@/lib/api';
import { API_URL } from '@/lib/consts';

type UserInfoContextType = {
  userInfo: DisplayUser | null;
  reloadUserInfo: () => Promise<void>;
  fetchUserInfo: () => Promise<DisplayUser | null>;
};

const UserInfoContext = createContext<UserInfoContextType | undefined>(undefined);

type UserInfoProviderProps = {
  children: React.ReactNode;
};

export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<DisplayUser | null>(null);

  /**
   * Fetches the current user's profile details for provider consumers.
   *
   * Returns a `Promise<DisplayUser | null>` that resolves to the authenticated user when the
   * protected `/user/info` probe succeeds. Treats HTTP 401 as the normal unauthenticated state,
   * clears the cached user, and returns `null` without logging an error. Logs unexpected non-OK
   * responses and network failures, clears the cached user, and returns `null` for those failures.
   */
  const fetchUserInfo = useCallback(async (): Promise<DisplayUser | null> => {
    try {
      const authCheckUrl = `${API_URL}/user/info`;
      const response = await fetch(authCheckUrl, { credentials: 'include' });
      if (response.status === 401) {
        // Treats the protected auth probe's unauthenticated response as the normal signed-out state.
        setUserInfo(null);
        return null;
      }
      if (!response.ok) {
        setUserInfo(null);
        console.error(
          'Error reloading user details:',
          new Error(`Failed to fetch user info: ${response.status}`)
        );
        return null;
      }
      const userDetails: DisplayUser = await response.json();
      setUserInfo(userDetails);
      return userDetails;
    } catch (error) {
      setUserInfo(null);
      console.error('Error reloading user details:', error);
      return null;
    }
  }, []);

  const reloadUserInfo = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  // Fetches user info on mount, including the expected unauthenticated probe result.
  React.useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  return (
    <UserInfoContext.Provider value={{ userInfo, reloadUserInfo, fetchUserInfo }}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = (): UserInfoContextType => {
  const context = useContext(UserInfoContext);
  if (context === undefined) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }
  return context;
};
